/**
 * Centralized Authentication Service
 * Handles all token operations with proper security measures
 */

// Storage keys - keeping existing keys for compatibility
const AUTH_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  USER_ID: 'user_id',
  STORAGE_TYPE: 'auth_storage_type', // Track which storage was used
  PLAN_STATUS: 'plan_status' // Non-auth data that shouldn't be cleared
};

// Grace period for token expiration (5 minutes)
const TOKEN_GRACE_PERIOD = 5 * 60 * 1000;

// Warning period before token expires (10 minutes)
const TOKEN_WARNING_PERIOD = 10 * 60 * 1000;

// Activity tracking
const ACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
const ACTIVITY_EXTENSION_THRESHOLD = 15 * 60 * 1000; // Extend if less than 15 minutes remaining

// Mutex to prevent concurrent token operations
let tokenOperationInProgress = false;
const tokenOperationQueue = [];

class AuthService {
  constructor() {
    this.tokenCheckInterval = null;
    this.expirationWarningCallback = null;
    this.logoutCallback = null;
    this.lastActivity = Date.now();
    this.activityListeners = [];
  }

  /**
   * Initialize auth service with callbacks
   */
  init(callbacks = {}) {
    this.expirationWarningCallback = callbacks.onExpirationWarning;
    this.logoutCallback = callbacks.onLogout;
  }

  /**
   * Decode JWT payload safely
   */
  decodeToken(token) {
    if (!token || typeof token !== 'string') return null;
    
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  /**
   * Validate JWT structure and basic properties
   */
  validateTokenStructure(token) {
    if (!token || typeof token !== 'string') return false;
    
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    try {
      // Validate header
      const header = JSON.parse(atob(parts[0]));
      if (!header.alg || !header.typ) return false;
      
      // Validate payload
      const payload = JSON.parse(atob(parts[1]));
      
      // Only check for expiration - be more lenient on other claims
      if (!payload.exp) return false;
      
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if token is expired with grace period
   */
  isTokenExpired(token) {
    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) return true;
    
    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();
    
    // Add grace period to handle clock skew
    return currentTime > (expirationTime + TOKEN_GRACE_PERIOD);
  }

  /**
   * Get time until token expires
   */
  getTokenTimeRemaining(token) {
    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) return 0;
    
    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();
    
    return Math.max(0, expirationTime - currentTime);
  }

  /**
   * Check if token is near expiration
   */
  isTokenNearExpiration(token) {
    const timeRemaining = this.getTokenTimeRemaining(token);
    return timeRemaining > 0 && timeRemaining <= TOKEN_WARNING_PERIOD;
  }

  /**
   * Get the storage type (localStorage or sessionStorage)
   */
  getStorageType() {
    const storageType = localStorage.getItem(AUTH_KEYS.STORAGE_TYPE);
    
    if (storageType === 'session') return sessionStorage;
    if (storageType === 'local') return localStorage;
    
    // Fallback: check where tokens exist
    if (sessionStorage.getItem(AUTH_KEYS.ACCESS_TOKEN)) return sessionStorage;
    return localStorage;
  }

  /**
   * Get token from appropriate storage
   */
  getAccessToken() {
    const storage = this.getStorageType();
    return storage.getItem(AUTH_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get refresh token from appropriate storage
   */
  getRefreshToken() {
    const storage = this.getStorageType();
    return storage.getItem(AUTH_KEYS.REFRESH_TOKEN);
  }

  /**
   * Get user data from appropriate storage
   */
  getUserData() {
    const storage = this.getStorageType();
    const data = storage.getItem(AUTH_KEYS.USER_DATA);
    if (!data) return null;
    
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('Failed to parse user data:', error);
      return null;
    }
  }

  /**
   * Store authentication data
   */
  storeAuthData(data, rememberMe = false) {
    const storage = rememberMe ? localStorage : sessionStorage;
    
    // Store tokens
    if (data.accessToken) {
      storage.setItem(AUTH_KEYS.ACCESS_TOKEN, data.accessToken);
    }
    if (data.refreshToken) {
      storage.setItem(AUTH_KEYS.REFRESH_TOKEN, data.refreshToken);
    }
    if (data.userId) {
      storage.setItem(AUTH_KEYS.USER_ID, data.userId);
    }
    if (data.userData) {
      storage.setItem(AUTH_KEYS.USER_DATA, JSON.stringify(data.userData));
    }
    
    // Track which storage was used
    localStorage.setItem(AUTH_KEYS.STORAGE_TYPE, rememberMe ? 'local' : 'session');
  }

  /**
   * Clear only authentication-related data
   */
  clearAuthData() {
    // Clear from both storages to be sure
    Object.values(AUTH_KEYS).forEach(key => {
      // Skip non-auth keys
      if (key === AUTH_KEYS.PLAN_STATUS) return;
      
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  }

  /**
   * Validate current authentication state
   */
  validateAuth() {
    const token = this.getAccessToken();
    
    if (!token) {
      return { isValid: false, reason: 'no_token' };
    }
    
    // Basic token structure validation (lenient)
    if (!this.validateTokenStructure(token)) {
      return { isValid: false, reason: 'invalid_token' };
    }
    
    if (this.isTokenExpired(token)) {
      return { isValid: false, reason: 'token_expired' };
    }
    
    if (this.isTokenNearExpiration(token)) {
      return { isValid: true, nearExpiration: true };
    }
    
    return { isValid: true };
  }

  /**
   * Execute token operation with mutex
   */
  async executeWithMutex(operation) {
    return new Promise((resolve, reject) => {
      const execute = async () => {
        if (tokenOperationInProgress) {
          tokenOperationQueue.push({ operation, resolve, reject });
          return;
        }
        
        tokenOperationInProgress = true;
        
        try {
          const result = await operation();
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          tokenOperationInProgress = false;
          
          // Process next in queue
          if (tokenOperationQueue.length > 0) {
            const next = tokenOperationQueue.shift();
            execute.call(null, next.operation).then(next.resolve).catch(next.reject);
          }
        }
      };
      
      execute();
    });
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken() {
    return this.executeWithMutex(async () => {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        
        if (!response.ok) {
          throw new Error('Token refresh failed');
        }
        
        const data = await response.json();
        
        // Store new tokens in the same storage as before
        const storage = this.getStorageType();
        storage.setItem(AUTH_KEYS.ACCESS_TOKEN, data.access_token);
        if (data.refresh_token) {
          storage.setItem(AUTH_KEYS.REFRESH_TOKEN, data.refresh_token);
        }
        
        return data.access_token;
      } catch (error) {
        // Clear auth data on refresh failure
        this.clearAuthData();
        throw error;
      }
    });
  }

  /**
   * Start monitoring token expiration
   */
  startTokenMonitoring() {
    // Clear any existing interval
    this.stopTokenMonitoring();
    
    // Setup activity listeners
    this.setupActivityListeners();
    
    // Check every 30 seconds
    this.tokenCheckInterval = setInterval(async () => {
      const authState = this.validateAuth();
      
      if (!authState.isValid) {
        this.stopTokenMonitoring();
        if (this.logoutCallback) {
          this.logoutCallback(authState.reason);
        }
        return;
      }

      // Check for inactivity
      if (this.isInactive()) {
        this.stopTokenMonitoring();
        if (this.logoutCallback) {
          this.logoutCallback('inactivity');
        }
        return;
      }

      // Handle near expiration
      if (authState.nearExpiration) {
        const token = this.getAccessToken();
        const timeRemaining = this.getTokenTimeRemaining(token);
        
        // Try to extend session if user is active and token is close to expiry
        if (timeRemaining <= ACTIVITY_EXTENSION_THRESHOLD) {
          try {
            await this.refreshAccessToken();
          } catch (error) {
            console.warn('Failed to extend session:', error);
          }
        }
        
        // Still show warning
        if (this.expirationWarningCallback) {
          this.expirationWarningCallback(timeRemaining);
        }
      }
    }, 30000);
  }

  /**
   * Stop monitoring token expiration
   */
  stopTokenMonitoring() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
    
    // Remove activity listeners
    this.removeActivityListeners();
  }

  /**
   * Handle authentication for API calls
   */
  async getAuthHeaders() {
    const token = this.getAccessToken();
    
    if (!token) {
      throw new Error('Authentication required');
    }

    if (this.isTokenExpired(token)) {
      try {
        const newToken = await this.refreshAccessToken();
        return { Authorization: `Bearer ${newToken}` };
      } catch (error) {
        throw new Error('Authentication required');
      }
    }
    
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * Login user
   */
  login(accessToken, refreshToken, userId, userData, rememberMe = false) {
    this.storeAuthData({
      accessToken,
      refreshToken,
      userId,
      userData
    }, rememberMe);
    
    // Start monitoring token expiration
    this.startTokenMonitoring();
  }

  /**
   * Logout user
   */
  logout() {
    // Stop monitoring
    this.stopTokenMonitoring();
    
    // Clear auth data
    this.clearAuthData();
  }

  /**
   * Update last activity timestamp
   */
  updateActivity() {
    this.lastActivity = Date.now();
  }

  /**
   * Check if user has been inactive too long
   */
  isInactive() {
    return (Date.now() - this.lastActivity) > ACTIVITY_TIMEOUT;
  }

  /**
   * Setup activity listeners
   */
  setupActivityListeners() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const activityHandler = () => {
      this.updateActivity();
    };
    
    events.forEach(event => {
      document.addEventListener(event, activityHandler, true);
      this.activityListeners.push({ event, handler: activityHandler });
    });
  }

  /**
   * Remove activity listeners
   */
  removeActivityListeners() {
    this.activityListeners.forEach(({ event, handler }) => {
      document.removeEventListener(event, handler, true);
    });
    this.activityListeners = [];
  }

  /**
   * Handle cross-tab logout synchronization
   */
  enableCrossTabSync() {
    window.addEventListener('storage', (event) => {
      // Check if auth token was removed
      if (event.key === AUTH_KEYS.ACCESS_TOKEN && !event.newValue) {
        if (this.logoutCallback) {
          this.logoutCallback('cross_tab_logout');
        }
      }
    });
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;